// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 36-edge-routing-dns-tls.md

import type { PipelineCheckStatus } from '@/components/greenlight/PipelineCheckRow';
import type { ControlPlaneHttpsVerify } from '@greenlight/contract';

export type CheckKey = 'ns_precheck' | 'delegation' | 'certificate' | 'https';

export const CHECK_ORDER: readonly CheckKey[] = [
  'ns_precheck',
  'delegation',
  'certificate',
  'https',
];

export const CHECK_LABELS: Record<CheckKey, string> = {
  ns_precheck: 'NS records resolve',
  delegation: 'Delegation confirmed',
  certificate: 'Certificate ready',
  https: 'HTTPS healthz',
};

/** A private install proves the same four steps differently: no public answer, and an
 * in-cluster health check in place of the public HTTPS request. */
export const PRIVATE_CHECK_LABELS: Record<CheckKey, string> = {
  ns_precheck: 'NS records resolve',
  delegation: 'No public address published',
  certificate: 'Certificate ready',
  https: 'Control plane answers in the cluster',
};

export type CheckResult = ControlPlaneHttpsVerify['checks'][CheckKey];

/** `not_ready` is the healthy waiting state of a live install (delegation still
 * propagating, cert-manager still issuing) — never a red failure. */
export function isRealFailure(check: CheckResult): boolean {
  return check.status === 'fail' && check.category !== 'not_ready';
}

export function checkStatus(
  check: CheckResult | undefined,
  checking: boolean,
  httpsVerified: boolean,
): PipelineCheckStatus {
  if (httpsVerified && check === undefined) return 'pass';
  if (check === undefined) return checking ? 'running' : 'skipped';
  if (check.status === 'pass') return 'pass';
  if (check.status === 'pending') return 'skipped';
  return isRealFailure(check) ? 'fail' : 'running';
}

/**
 * Once NS / delegation have passed, a later poll that short-circuits on a
 * stale NXDOMAIN / SERVFAIL must not overwrite those rows (or drop certificate
 * progress back to pending). First failure still stands; only a confirmed
 * success freezes.
 */
export function latchDnsSuccess(
  previous: ControlPlaneHttpsVerify | null,
  next: ControlPlaneHttpsVerify,
): ControlPlaneHttpsVerify {
  if (!previous) return next;
  const nsLatched = previous.checks.ns_precheck.status === 'pass';
  const delegationLatched = previous.checks.delegation.status === 'pass';
  if (!nsLatched && !delegationLatched) return next;

  const nsNowDown =
    next.checks.ns_precheck.status !== 'pass' || next.checks.delegation.status !== 'pass';
  if (!nsNowDown) return next;

  return {
    ok: previous.ok || next.ok,
    checks: {
      ns_precheck: nsLatched ? previous.checks.ns_precheck : next.checks.ns_precheck,
      delegation: delegationLatched ? previous.checks.delegation : next.checks.delegation,
      certificate:
        next.checks.certificate.status === 'pending'
          ? previous.checks.certificate
          : next.checks.certificate,
      https: next.checks.https.status === 'pending' ? previous.checks.https : next.checks.https,
    },
  };
}

export function announce(
  result: ControlPlaneHttpsVerify | null,
  allPassed: boolean,
  labels: Record<CheckKey, string> = CHECK_LABELS,
): string {
  if (allPassed) return 'HTTPS is ready.';
  if (!result) return '';
  const failed = CHECK_ORDER.map((k) => result.checks[k]).find(isRealFailure);
  if (failed) return failed.detail;
  const lastPassed = [...CHECK_ORDER].reverse().find((k) => result.checks[k].status === 'pass');
  return lastPassed ? `${labels[lastPassed]}.` : '';
}
